/**
 * The assistant's blob (migration 0067): a face the site picked from the ones the dashboard offers,
 * drawn with blobatar (MIT, pinned at 2.7.0). It breathes and blinks, its eyes follow the cursor,
 * and its expression follows the conversation.
 *
 * 🔴 blobatar/internal IS AN ADAPTER'S API, NOT A CONSUMER'S. Its string API draws only still
 *    blobs, and an animated one without a framework is `_parts`, which is what its own React and
 *    Vue adapters call. Its shape may change on a major, which is why the version is pinned exactly
 *    and blob.test.ts pins the shape: an upgrade that breaks it fails a test, not a customer's page.
 *
 * 🔴 @property DOES NOTHING INSIDE A SHADOW ROOT. Both stylesheets register their custom properties
 *    with it, and browsers ignore the rule in a shadow tree, so the expressions would jump instead
 *    of morphing and the eyes would never follow anything. registerBlobProperties does the same
 *    registration from script, which is document-wide and does reach the shadow root.
 */
import { _parts } from "blobatar/internal";
import { happy, idle, thinking, unsure } from "blobatar/expression";
import { gaze, type Gaze } from "blobatar/gaze";
import motionCss from "blobatar/motion.css?raw";
import gazeCss from "blobatar/gaze.css?raw";

export type Mood = "idle" | "thinking" | "happy" | "unsure";
const POSES = { idle, thinking, happy, unsure } as const;

/** The two stylesheets, for the shadow root, without the rules it would ignore anyway. */
export const blobStyles = (motionCss + gazeCss).replace(/@property[^{]*\{[^}]*\}/g, "");

let registered = false;
export function registerBlobProperties(): void {
  if (registered) return;
  registered = true;
  if (typeof CSS === "undefined" || typeof CSS.registerProperty !== "function") return;
  for (const m of (motionCss + gazeCss).matchAll(/@property\s*(--[\w-]+)\s*\{([^}]*)\}/g)) {
    const body = m[2] ?? "";
    try {
      CSS.registerProperty({
        name: m[1]!,
        syntax: /syntax:\s*"([^"]+)"/.exec(body)?.[1] ?? "*",
        inherits: /inherits:\s*true/.test(body),
        initialValue: /initial-value:\s*([^;]+)/.exec(body)?.[1]?.trim(),
      });
    } catch {
      // Already registered, by the host page using blobatar itself. Same package, same definitions.
    }
  }
}

const SVG = "http://www.w3.org/2000/svg";
/**
 * How far the eyes travel toward the cursor, in viewBox units. blobatar suggests 1.5 to 4, which is
 * for a face looked at closely; at 3 the button's eyes moved about a screen pixel and read as not
 * following at all (2026-10-29). The driver keeps them on the face at any travel.
 */
const TRAVEL = "6px";

/** A blob that moves: kept so a mood can reach it and its cursor tracking can be stopped. */
interface Live {
  svg: SVGSVGElement;
  g: SVGGElement;
  seed: string;
  keys: string[];
  gaze: Gaze | null;
}
const living = new Set<Live>();

function frame(seed: string, animate: boolean, mood: Mood) {
  // No plate: the body alone. A round plate around it read as a small face in a white disc (Vishnu,
  // 2026-10-29: "that background white area... makes it not cool").
  return _parts(seed, {
    ...(animate ? { animate: "always" as const } : {}),
    ...(mood === "idle" ? {} : { expression: POSES[mood] }),
  });
}

/**
 * One blob, as an <svg> that fills its box. `live` makes it move and follow the cursor; a still one
 * is for the transcript, where every reply wearing the same moving face would blink in unison.
 */
export function drawBlob(seed: string, live: boolean, mood: Mood = "idle"): SVGSVGElement {
  const parts = frame(seed, live, mood);
  const svg = document.createElementNS(SVG, "svg");
  svg.setAttribute("viewBox", "0 0 100 100");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("blob");
  for (const [k, v] of Object.entries(parts.vars ?? {})) svg.style.setProperty(k, v);
  const g = document.createElementNS(SVG, "g");
  if (parts.cls) g.setAttribute("class", parts.cls);
  // blobatar's own markup from a seed the server checked against ^[a-z0-9-]{1,40}$; the seed is
  // never written into it.
  g.innerHTML = parts.inner;
  svg.appendChild(g);
  if (live) living.add({ svg, g, seed, keys: Object.keys(parts.vars ?? {}), gaze: null });
  return svg;
}

/** Starts following the cursor, once the blob is on the page and can be measured. */
export function followPointer(svg: SVGSVGElement): void {
  for (const b of living) {
    if (b.svg !== svg || b.gaze) continue;
    svg.style.setProperty("--mo-track-travel", TRAVEL);
    b.gaze = gaze(svg, { target: "pointer" });
  }
}

/**
 * Every moving blob takes this face. The same svg and the same <g>, so the CSS transition morphs
 * from the old pose instead of a new element starting from nothing. Blobs no longer on the page are
 * let go here, cursor listeners and all.
 */
export function setMood(mood: Mood): void {
  for (const b of living) {
    if (!b.svg.isConnected) {
      b.gaze?.stop();
      living.delete(b);
      continue;
    }
    const parts = frame(b.seed, true, mood);
    const vars = parts.vars ?? {};
    for (const k of b.keys) if (!(k in vars)) b.svg.style.removeProperty(k);
    for (const [k, v] of Object.entries(vars)) b.svg.style.setProperty(k, v);
    b.keys = Object.keys(vars);
    b.g.setAttribute("class", parts.cls ?? "");
  }
}
